import React, { Component } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { Text, Box, Grid, GridItem, SimpleGrid, Tabs, TabList, TabPanels, Tab, TabPanel, Table, HStack, VStack } from '@chakra-ui/react';

import DetailErrorTable from "./DetailErrorTable";

/**
 * Filters an array of objects, returning only those where a specified property's value is true.
 *
 * @param {Array<Object>} arr - The array of objects to filter.
 * @param {string} propName - The name of the property to check for a 'true' value.
 * @returns {Array<Object>} A new array containing only the objects where the specified property is true.
 */
function filterByPropertyVal(arr, propName, propVal) {
  if (!Array.isArray(arr)) {
    console.error("The first argument must be an array.");
    return [];
  }
  if (typeof propName !== 'string' || propName.trim() === '') {
    console.error("The second argument must be a non-empty string representing the property name.");
    return [];
  }

  return arr.filter(obj => obj.hasOwnProperty(propName) && obj[propName] === propVal);
}

/**
 * Checks if a given string can be successfully parsed into a JavaScript object (JSON).
 *
 * @param {string} str - The string to check.
 * @returns {boolean} True if the string is valid JSON and can be parsed into an object, false otherwise.
 */
function isValidJsonObjectString(str) 
{
  if (typeof str !== 'string' || str.trim() === '') {
    // Not a string or an empty string, so it cannot be a valid JSON object string.
    return false;
  }
  try {
    const parsed = JSON.parse(str);
    // Check if the parsed result is actually an object (and not null, which is also typeof 'object')
    return typeof parsed === 'object' && parsed !== null;
  } catch (e) {
    // If JSON.parse throws an error, it's not a valid JSON string.
    return false;
  }
}
function parseSingleCodeMessageErrorObject(errorObject) 
{
  var result = [];
  // tbd - assert that the errorObject is what we expect
  // this assumes an errorObject that contains just an errors array property {code: xxx, message: xxx} and a metdata property {code: xxx, operationId: xxx, operationTs: xxx} 
  errorObject.errors.forEach((error) => {
    var flatError = {
      code: error.code || "400",
      message: error.message || "Unknown error",
      transactionType: errorObject.metadata?.code || "Unknown",
      transactionId: errorObject.metadata?.operationId || null,
      transactionT: errorObject.metadata?.operationTs || null,
      errorSource: null // this
    };
    result.push(flatError);
  });
  return result;
}

function parseMultipleCodeMessageErrorObjects(errorObjects)
{
  var result = [];
  // tbd - assert that the errorObjects is what we expect
  errorObjects.forEach((errorObject) => 
  {
    if (errorObject.hasOwnProperty("hasError") && errorObject.hasError) 
    {
      errorObject.messages.forEach((message) => 
      {
        if (isValidJsonObjectString(message))
        {
          var parsedError = JSON.parse(message);
          result.push(...parseSingleCodeMessageErrorObject(parsedError));
        }
      });
    }
  });
  return result;
}

function parseSpecialTypeMessageErrorObjects(errorObject)
{
  // tbd - assert that the errorObject is what we expect
  var result = [];
  if (errorObject.errors)
    {
      var errorPropertyNames = Object.keys(errorObject.errors);
      errorPropertyNames.forEach((propertyName) => 
      { 
        // map each objectJson.errors[propertyName] to an object with code = objectJson.type and message = propertyName + ": " + objectJson.errors[propertyname] value
        var errorValue = errorObject.errors[propertyName];
        if (Array.isArray(errorValue)) 
        {
          errorValue.forEach((error) => 
          {
            var flatError = 
            {
              code: error.code || "400",
              message: errorObject.title + " " + propertyName + " generated error: " + error || "Unknown error",
              transactionType: errorObject.type || "Unknown",
              transactionId: errorObject.traceId || null,
              transactionT: null,
              errorSource: null 
            };
            result.push(flatError);
          });
        }
      });
    }
    return result;
}

function unknownErrorObject(jsonObject)
{
  var flatError = 
  {
    code: "400",
    message: "Unidentified error format: " + JSON.stringify(jsonObject),
    transactionType: "Unknown",
    transactionId: null,
    transactionT: null,
    errorSource: null 
  };
  return flatError;
}




/**
 * Looks at error messages result and figure out which parsing method to use to generate an array of  "standard" error ojbects
 *@param {object} jsonObject 
 * @returns {Array} An array of error objects with the following properties: code, message, transactionType, transactionId, transactionT
 */
function errorsJsonFactory(jsonObject) 
{
  var result = [];

  if (jsonObject && jsonObject.hasOwnProperty("metadata") && jsonObject.metadata)
  {
    result.push(...parseSingleCodeMessageErrorObject(jsonObject));
  }
  else if (Array.isArray(jsonObject) && jsonObject.length > 0)
  {
    result.push(...parseMultipleCodeMessageErrorObjects(jsonObject));
  }
  else if (jsonObject && jsonObject.hasOwnProperty("title") && jsonObject.hasOwnProperty("type")) 
  {
    result.push(...parseSpecialTypeMessageErrorObjects(jsonObject))
  }
  else
  {
    result.push(unknownErrorObject(jsonObject));
  }
  return result;
}




function toItemTracerPostErrors(responseMessages) 
{
  var errorMessages = [];
  if (!responseMessages || responseMessages.length === 0) return errorMessages;
  if (!Array.isArray(responseMessages)) return errorMessages;
  var areErrors = responseMessages.some((message) => {
  if (message.hasOwnProperty("hasError")) 
  {
      return message.hasError;
  }
  return false;
  });
  if (!areErrors) return errorMessages;
  var responseErrorMessages = filterByPropertyVal(responseMessages, "hasError", true);
  if (!responseErrorMessages || responseErrorMessages.length === 0) return errorMessages;
  for (let i = 0; i < responseErrorMessages.length; i++)
  {
    var errorMessage = responseErrorMessages[i];
    if (!errorMessage.hasOwnProperty("messages") || errorMessage.messages.length > 0)
    {
      for (let j = 0; j < errorMessage.messages.length; j++)
      {
        var errorMessageMessage = errorMessage.messages[j];
        // confirm we are on a message that can be parsed into a valid JSON object
        if (!isValidJsonObjectString(errorMessageMessage)) continue;
        // parse it!
        var errorMessageJson = JSON.parse(errorMessageMessage);
        if (errorMessageJson && errorMessageJson.hasOwnProperty("metadata") && errorMessageJson.metadata) 
        {
          var errorObj = {
            errors: errorMessageJson.errors ?? [],
            metadata: errorMessageJson.metadata || null
          };
          errorMessages.push(errorObj);
        }
      }
    }
  }
  return errorMessages;
}


export default class SummaryErrorDisplay extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedRow: null,
            rowKeys: '',
            selectedItemRow: null
        };

        this.itemRowChange = this.itemRowChange.bind(this);
        this.parseErrors = this.parseErrors.bind(this);
    }



    itemRowChange(newItemRow) {
        this.setState({ selectedItemRow: newItemRow });
    }

    parseErrors() {
      var result = [];
      var hasMessages = (this.props.selectedRow) ? this.props.selectedRow.messages : null;
      if (hasMessages && hasMessages.length > 0)
      {
        for (let i = 0; i < this.props.selectedRow.messages.length; i++)
        {
          var message = this.props.selectedRow.messages[i];
          if (!isValidJsonObjectString(message)) continue;
          var objectJson = JSON.parse(message);
          var flattenedErrors = errorsJsonFactory(objectJson);
          if (flattenedErrors && flattenedErrors.length > 0)
          {    
            console.info(flattenedErrors);
            result.push(...flattenedErrors);
          }
        }
      }
      return result || []; // return an empty array if no errors found
    }




    render() {

        var errorTableData = this.parseErrors();

        return (

            <VStack w="100%">
                <DetailErrorTable
                    //selectedRow={this.props.selectedRow}
                    errorTableData = {errorTableData}
                />
            </VStack>
        );
    }
}
